import React, { Component } from 'react'
import { connect } from 'react-redux'
import debounce from 'lodash.debounce'
import PropTypes from 'prop-types'
import NumberFormat from 'react-number-format'
import { colors, midas } from '@resurs/midas'
import {
  Button,
  Column,
  Columns,
  Icon,
  Modal,
  NumberInput,
  Select,
} from '@resurs/midas-react'
import { faExclamationCircle } from '@fortawesome/free-solid-svg-icons'
import Banner from '../../../../components/CommonUI/Banner/Banner'
import {
  isExistingPartPayment,
  isResursPaymentMethod,
} from '../../../../models/payment-methods'
import {
  checkPaymentMethodCostAlternatives,
  getStorePaymentMethods,
  resetCostAlternatives,
  getPaymentPerMonth,
} from '../../../../store/actions/pay-apply-actions'
import { getText } from '../../../../internal/TextLabels'
import { ChannelType } from '../../../../models/channelType'
import { sendPriceInfoMail } from '../../../../store/actions/price-info-mail-actions'
import { PaymentPerMonthOptions } from './PaymentPerMonthOptions'
import { TermsAndSekkiCard } from './TermsAndSekkiCard'
import { DynamicPaymentPlans } from './DynamicPaymentPlans'
import analytics, {
  GAEvents,
  GACategories,
  GAActions,
} from '../../../../util/analytics'

const getAmountLowerRange = ({
  repaymentMonths,
  minPurchaseLimit,
  minApplicationLimit,
}) =>
  repaymentMonths && repaymentMonths.length > 0
    ? minApplicationLimit
    : minPurchaseLimit

const getAmountUpperRange = ({ maxApplicationLimit }) => maxApplicationLimit

const isAmountInRange = (amount, method) =>
  !!amount &&
  !!method &&
  getAmountLowerRange(method) <= amount &&
  getAmountUpperRange(method) >= amount

class PaymentPerMonth extends Component {
  constructor(props) {
    super(props)
    this.state = {
      show: false,
      amount: props.amount || '',
      method: props.method || null,
      inRange: isAmountInRange(props.amount, props.method),
    }
    this.debouncedFetchCostMatrix = debounce(this.fetchCostMatrix, 300)
  }

  onClose() {
    this.setState({ show: false })
    this.props.resetCostAlternatives()
  }

  onChangeMethod(methodId) {
    const method = this.props.methods.filter(m => m.id === methodId)[0]

    analytics.sendEvent(
      GAEvents.APPLICATION_FLOW,
      GAActions.RESULTS,
      GACategories.applicationFlow.CALCULATOR,
      method.description,
    )

    if (
      (!this.state.method || methodId !== this.state.method.id) &&
      isAmountInRange(this.state.amount, method)
    ) {
      this.setState({ method, inRange: true }, () => {
        this.props.getPaymentPerMonth(
          this.state.method.id,
          this.state.amount,
          method.repaymentMonths && Math.max(...method.repaymentMonths),
        )
      })
    } else {
      this.setState(state => ({
        method,
        inRange: isAmountInRange(state.amount, method),
      }))
    }
  }

  onChangeAmount(value) {
    if (isAmountInRange(value, this.state.method)) {
      this.setState({ amount: value, inRange: true }, () => {
        this.debouncedFetchCostMatrix()
      })
    } else {
      this.setState({ amount: value, inRange: false })
    }
  }

  fetchCostMatrix() {
    if (this.state.method.repaymentMonths) {
      this.props.getPaymentMethodCostAlternatives(
        this.state.amount,
        this.state.method.id,
      )
    } else if (this.state.amount && this.state.inRange) {
      this.props.getPaymentPerMonth(this.state.method.id, this.state.amount)
    }
  }

  paymentMethodValues() {
    return (this.props.personalizedPaymentMethods || this.props.methods)
      .filter(m => isResursPaymentMethod(m.type) && !isExistingPartPayment(m))
      .map(m => ({
        key: m.id,
        value: m.id,
        valueLabel: m.description,
      }))
  }

  openModal() {
    analytics.sendEvent(
      GAEvents.APPLICATION_FLOW,
      GAActions.IMPRESSION,
      GACategories.applicationFlow.CALCULATOR,
      'Calculator popup',
    )
    this.setState(
      {
        method: this.props.method,
        amount: this.props.amount || '',
      },
      () => {
        this.props.getStorePaymentMethods({
          typeFilters: ['NEW'],
          customerType: this.props.customerType,
          channel: ChannelType.MERCHANT_PORTAL,
        })

        if (
          this.state.method &&
          this.state.method.id &&
          this.state.amount &&
          this.state.inRange
        ) {
          this.props.getPaymentPerMonth(this.state.method.id, this.state.amount)
        }

        this.setState({ show: true })
        this.onChangeAmount(this.props.amount)
      },
    )
  }

  shouldShowCalculateButton() {
    return (
      this.props.paymentMethodsExist &&
      (!this.props.selectedPaymentMethod ||
        !this.props.selectedPaymentMethod.repaymentMonths)
    )
  }

  render() {
    return (
      <div>
        {this.shouldShowCalculateButton() && (
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              marginRight: '-1.9rem',
            }}
          >
            <Icon
              name="midasCalculator3"
              style={{
                display: 'flex',
                color: colors.primaryMain,
                marginRight: '-1rem',
              }}
            />
            <Button
              tabIndex={-1}
              id="payment-per-month"
              onClick={() => this.openModal()}
              text={getText('payApply.paymentPerMonth.calculateAmount')}
              size="small"
              type="link"
              disabled={this.props.disabled}
            />
          </div>
        )}
        <Modal
          show={this.state.show}
          title={getText('payApply.paymentPerMonth.calculateAmount')}
          onClose={() => this.onClose()}
          actions={
            <span>
              <Button
                size="small"
                type="primary"
                text={getText('payApply.paymentPerMonth.close')}
                onClick={() => this.onClose()}
              />
            </span>
          }
        >
          {!this.props.methods ? (
            <div className={midas.loading} />
          ) : this.props.methods.length < 1 ? (
            <Banner
              faIcon={faExclamationCircle}
              type="danger"
              text={getText('payApply.paymentPerMonth.noPaymentMethods')}
            />
          ) : (
            <div>
              <div style={{ marginBottom: '2rem' }}>
                {!this.state.method &&
                  getText('payApply.paymentPerMonth.formInstructions')}
                {this.state.method && (
                  <span>
                    {getText('payApply.paymentPerMonth.amountLimits')}
                    <NumberFormat
                      value={getAmountLowerRange(this.state.method)}
                      thousandSeparator=" "
                      displayType="text"
                      suffix=" - "
                    />
                    <NumberFormat
                      value={getAmountUpperRange(this.state.method)}
                      thousandSeparator=" "
                      displayType="text"
                      renderText={val => getText('default.currency', val)}
                    />
                  </span>
                )}
              </div>
              <Columns>
                <Column col6 colMd12>
                  <Select
                    id="pMethodSelect"
                    label={getText('payApply.paymentPerMonth.paymentMethod')}
                    value={
                      this.state.method && this.state.method.id
                        ? this.state.method.id
                        : ''
                    }
                    optionsLabel={
                      !this.state.method || !this.state.method.id
                        ? getText(
                            'payApply.paymentPerMonth.paymentMethodOptions',
                          )
                        : null
                    }
                    onChange={value => this.onChangeMethod(value)}
                    values={this.paymentMethodValues()}
                    required={getText(
                      'payApply.paymentPerMonth.validationMessageMethod',
                    )}
                  />
                </Column>
                <Column col6 colMd12>
                  <NumberInput
                    id="amount"
                    label={getText('payApply.paymentPerMonth.amount')}
                    placeholder={getText(
                      'payApply.paymentPerMonth.amountPlaceholder',
                    )}
                    required={getText(
                      'payApply.paymentPerMonth.validationMessageAmount',
                    )}
                    value={`${this.state.amount || ''}`}
                    onChange={value => this.onChangeAmount(value)}
                    disableAutocomplete
                    thousandSeparator=" "
                  />
                </Column>
              </Columns>
              {this.state.inRange && (
                <div>
                  {this.state.method &&
                    this.state.amount &&
                    !this.state.method.repaymentMonths && (
                      <PaymentPerMonthOptions
                        methodId={this.state.method.id}
                        description={this.state.method.description}
                        amount={this.state.amount}
                        cost={this.props.cost}
                        costLoading={this.props.costLoading}
                      />
                    )}

                  {this.state.method &&
                    this.state.amount &&
                    this.state.method.repaymentMonths && (
                      <DynamicPaymentPlans
                        costAlternatives={this.props.costAlternatives}
                        getPaymentPerMonth={this.props.getPaymentPerMonth}
                        monthlyCost={this.props.monthlyCost}
                        sendPriceInfoMail={this.props.sendPriceInfoMail}
                        methodDescription={this.state.method.description}
                        amount={this.state.amount}
                        methodId={this.state.method.id}
                        months={
                          this.state.method.repaymentMonths[
                            this.state.method.repaymentMonths.length - 1
                          ]
                        }
                      />
                    )}

                  {!this.props.costLoading &&
                    !this.props.cost &&
                    !(
                      this.state.method &&
                      this.state.amount &&
                      this.state.method.repaymentMonths
                    ) && (
                      <Column col12>
                        <div>
                          {getText('payApply.paymentPerMonth.noInfoAvailable')}
                        </div>
                      </Column>
                    )}
                  {(this.props.sekki || this.props.terms) &&
                    this.state.amount &&
                    this.state.method?.minApplicationLimit && (
                      <TermsAndSekkiCard
                        country={this.props.country}
                        customerType={this.props.customerType}
                        sekki={this.props.sekki}
                        terms={this.props.terms}
                      />
                    )}
                </div>
              )}
            </div>
          )}
        </Modal>
      </div>
    )
  }
}

const mapStateToProps = state => {
  return {
    sekki: state.payApply.paymentPerMonth.sekkiLinks,
    terms: state.payApply.paymentPerMonth.generalTermsLinks,
    cost: state.payApply.paymentPerMonth.costList,
    methods: state.payApply.paymentPerMonth.paymentMethods,
    personalizedPaymentMethods: state.payApply.paymentMethods,
    costLoading: state.payApply.paymentPerMonth.costLoading,
    selectedPaymentMethod: state.payApply.current.selectedPaymentMethod,
    costAlternatives: state.payApply.current.costAlternatives,
    monthlyCost: state.payApply.current.monthlyCost,
    country:
      (state.storeInfo.currentStore && state.storeInfo.currentStore.country) ||
      'SE',
  }
}

const mapDispatchToProps = dispatch => ({
  getPaymentPerMonth: (method, amount, months = null) => {
    dispatch(getPaymentPerMonth(method, amount, months))
  },
  getStorePaymentMethods: params => {
    dispatch(getStorePaymentMethods(params))
  },
  getPaymentMethodCostAlternatives: (amount, paymentMethodId) => {
    dispatch(checkPaymentMethodCostAlternatives(amount, paymentMethodId))
  },
  resetCostAlternatives: () => {
    dispatch(resetCostAlternatives())
  },
  sendPriceInfoMail: (paymentMethodId, periodOfRepayment, amount, email) => {
    dispatch(
      sendPriceInfoMail(paymentMethodId, periodOfRepayment, amount, email),
    )
  },
})

PaymentPerMonth.defaultProps = {
  method: null,
  methods: null,
  amount: null,
  sekki: null,
  terms: null,
  cost: null,
  personalizedPaymentMethods: null,
  disabled: false,
  country: 'SE',
}

PaymentPerMonth.propTypes = {
  method: PropTypes.object,
  methods: PropTypes.array,
  personalizedPaymentMethods: PropTypes.array,
  amount: PropTypes.number,
  customerType: PropTypes.string.isRequired,
  getPaymentPerMonth: PropTypes.func.isRequired,
  getStorePaymentMethods: PropTypes.func.isRequired,
  sekki: PropTypes.array,
  terms: PropTypes.array,
  cost: PropTypes.array,
  costLoading: PropTypes.bool.isRequired,
  disabled: PropTypes.bool,
  country: PropTypes.string,
  paymentMethodsExist: PropTypes.bool,
}

PaymentPerMonth.defaultProps = {
  paymentMethodsExist: false,
}

export default connect(mapStateToProps, mapDispatchToProps)(PaymentPerMonth)
